import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import SvgLoadingAnimation from "../../components/loading";
import Button from "../../components/button/Button";
import IconButton from "../../components/button/IconButton";
import ToolTip from "../../components/tooltip/ToolTip";
import BulkDownloadProgressModal from "../../components/message/BulkDownloadProgressModal";
import { getFileIconFromCategoryOrExtension } from "../../components/file-icon/fileIcon";
import { downloadFile } from "../../utils/downloadFile";
import { useBulkDownload } from "../../hooks/useBulkDownload";
import { FlatFileEntry } from "../../components/message/folderUtils";
import {
  ProjectMessageFile,
  SharedContentFile,
  useGetSharedContentQuery,
} from "../../app/services/projectMessages";
import {
  formatDateTime,
  formatFileSize,
} from "../talkroom/components/FileTable/fileTableUtils";
import NoteFolderShareView from "./NoteFolderShareView";

const PageContainer = styled.div.attrs({ className: "u-col-center u-w100" })`
  padding: 48px 16px 120px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 720px;
  padding: 24px;
  border-radius: 6px;
  border: 1px solid var(--soremo-border);
  background: #fff;
  box-shadow: 2px 4px 8px rgba(0, 0, 0, 0.05);
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;

  .material-symbols-rounded {
    font-size: 24px;
  }
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  border-bottom: 1px solid var(--soremo-border);

  &:last-child {
    border-bottom: none;
  }

  .material-symbols-rounded {
    font-size: 18px;
    flex-shrink: 0;
  }

  .share-file-path {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .share-file-size {
    flex-shrink: 0;
    color: var(--soremo-light-gray);
  }
`;

const MessageBlock = styled.div.attrs({ className: "u-col-center u-gap16" })`
  padding: 48px 0;
`;

/**
 * 共有リンクのランディングページ（/share/:token）。
 *
 * トークルーム / 共有ライブラリのファイル一覧から発行された共有リンクを
 * 開くと、対象ファイル（フォルダの場合は配下の全ファイル）をダウンロード
 * できる。共有ノートのフォルダ共有（type: note_folder）は、配下のノートを
 * 閲覧専用ビューア（NoteFolderShareView）で表示する。
 * アクセス制限付きリンクはサインイン + プロジェクトメンバーであることが必要。
 */
const SharePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useGetSharedContentQuery(
    { token: token! },
    { skip: !token },
  );

  // フォルダ共有の zip 一括ダウンロード（トークルームのファイル一覧と同じ仕組み）
  const {
    downloadState,
    isDownloading: isZipDownloading,
    startDownload,
    closeDownloadModal,
  } = useBulkDownload();

  // zip 用エントリ（フォルダ内の相対パスを保ったまま格納する）
  const zipEntries = useMemo<FlatFileEntry[]>(() => {
    const shared = data?.data;
    // ノートのフォルダ共有は閲覧専用ビューアで、ダウンロード対象を持たない
    if (!shared || shared.type === "note_folder") return [];
    return shared.files
      .filter((f) => f.url)
      .map((f) => ({
        path: f.path,
        file: {
          id: f.id,
          original_url: f.url!,
          file_name: f.name,
          file_type: (f.file_type ?? "other") as ProjectMessageFile["file_type"],
        } as ProjectMessageFile,
      }));
  }, [data]);

  const handleDownloadAll = useCallback(() => {
    const shared = data?.data;
    if (!shared || zipEntries.length === 0) return;
    startDownload(zipEntries, shared.name);
  }, [data, zipEntries, startDownload]);

  // ページ表示と同時にダウンロードを開始する
  // （単一ファイルは直接ダウンロード、フォルダは zip 一括ダウンロード）
  const autoDownloadStartedRef = useRef(false);
  useEffect(() => {
    if (autoDownloadStartedRef.current) return;
    const shared = data?.data;
    if (!shared || shared.type === "note_folder") return;
    if (shared.type === "file") {
      const file = shared.files[0];
      if (!file?.url) return;
      autoDownloadStartedRef.current = true;
      downloadFile(file.url, file.name);
    } else if (zipEntries.length > 0) {
      autoDownloadStartedRef.current = true;
      startDownload(zipEntries, shared.name);
    }
  }, [data, zipEntries, startDownload]);

  if (isLoading) {
    return (
      <PageContainer>
        <SvgLoadingAnimation />
      </PageContainer>
    );
  }

  if (error || !data) {
    const status = (error as { status?: number } | undefined)?.status;
    let message = "共有リンクが見つかりません";
    let showSignin = false;
    if (status === 410) {
      message = "この共有リンクは有効期限が切れています";
    } else if (status === 401) {
      message = "この共有リンクを開くにはサインインが必要です";
      showSignin = true;
    } else if (status === 403) {
      message = "この共有リンクへのアクセス権限がありません";
    }
    return (
      <PageContainer>
        <Card>
          <MessageBlock>
            <span className="material-symbols-rounded u-fontsize-48 u-text-light-gray">
              link_off
            </span>
            <p className="bodytext u-text-gray">{message}</p>
            {showSignin && (
              <Button size="small" onClick={() => navigate("/signin")}>
                サインイン
              </Button>
            )}
          </MessageBlock>
        </Card>
      </PageContainer>
    );
  }

  const shared = data.data;

  // 共有ノートのフォルダ共有: 閲覧専用ビューア（ダウンロードはしない）
  if (shared.type === "note_folder") {
    return (
      <PageContainer>
        <div className="u-col u-gap16 u-w100" style={{ maxWidth: 1080 }}>
          <div className="u-col u-gap8">
            <TitleRow>
              <span className="material-symbols-rounded">folder</span>
              <h1 className="heading-spacing-18" style={{ margin: 0, flex: 1 }}>
                {shared.name}
              </h1>
            </TitleRow>
            <p className="bodytext-11 u-text-light-gray" style={{ margin: 0 }}>
              {shared.project_name && `${shared.project_name} / `}
              {shared.expires_at
                ? `有効期限: ${formatDateTime(shared.expires_at)}`
                : "有効期限なし"}
            </p>
          </div>
          <NoteFolderShareView shared={shared} />
        </div>
      </PageContainer>
    );
  }

  const handleDownload = (file: SharedContentFile) => {
    if (!file.url) return;
    downloadFile(file.url, file.name);
  };

  return (
    <PageContainer>
      <Card>
        <TitleRow>
          <span className="material-symbols-rounded">
            {shared.type === "folder"
              ? "folder"
              : getFileIconFromCategoryOrExtension(
                  shared.files[0]?.file_type,
                  shared.name,
                )}
          </span>
          <h1 className="heading-spacing-18" style={{ margin: 0, flex: 1 }}>
            {shared.name}
          </h1>
          {shared.type === "folder" && zipEntries.length > 0 && (
            <ToolTip description="一括ダウンロード" placement="top">
              <IconButton
                icon="download"
                size={24}
                disabled={isZipDownloading}
                onClick={handleDownloadAll}
              />
            </ToolTip>
          )}
        </TitleRow>
        <p className="bodytext-11 u-text-light-gray" style={{ margin: "0 0 16px" }}>
          {shared.project_name && `${shared.project_name} / `}
          {shared.expires_at
            ? `有効期限: ${formatDateTime(shared.expires_at)}`
            : "有効期限なし"}
        </p>

        {shared.type === "file" ? (
          <p className="bodytext-13 u-text-gray" style={{ margin: "0 0 16px" }}>
            ダウンロードを開始しました。始まらない場合は下のボタンからダウンロードしてください。
          </p>
        ) : (
          zipEntries.length > 0 && (
            <p className="bodytext-13 u-text-gray" style={{ margin: "0 0 16px" }}>
              一括ダウンロードを開始しました。始まらない場合は右上のアイコンからダウンロードしてください。
            </p>
          )
        )}

        <div className="u-w100 u-col">
          {shared.files.length === 0 ? (
            <p className="bodytext-13 u-text-light-gray">
              ダウンロードできるファイルがありません
            </p>
          ) : (
            shared.files.map((file) => (
              <FileRow key={file.id}>
                <span className="material-symbols-rounded">
                  {getFileIconFromCategoryOrExtension(file.file_type, file.name)}
                </span>
                <span className="share-file-path bodytext-13">{file.path}</span>
                <span className="share-file-size bodytext-11">
                  {formatFileSize(file.size)}
                </span>
                <ToolTip description="ダウンロード" placement="top">
                  <IconButton
                    icon="download"
                    size={20}
                    disabled={!file.url}
                    onClick={() => handleDownload(file)}
                  />
                </ToolTip>
              </FileRow>
            ))
          )}
        </div>
      </Card>

      {/* zip 一括ダウンロードの進捗モーダル */}
      {downloadState && (
        <BulkDownloadProgressModal
          state={downloadState}
          downloadName={shared.name}
          onClose={closeDownloadModal}
        />
      )}
    </PageContainer>
  );
};

export default SharePage;
